import { useEffect, useRef, useState } from "react";

// Premium bright luxury imagery — Mediterranean / coastal / aerial villas
const HERO_IMAGES = [
  "https://images.unsplash.com/photo-1613490493576-7fde63acd811?auto=format&fit=crop&w=2400&q=80",
  "https://images.unsplash.com/photo-1613977257363-707ba9348227?auto=format&fit=crop&w=2400&q=80",
  "https://images.unsplash.com/photo-1601581875309-fafbf2d3ed3a?auto=format&fit=crop&w=2400&q=80",
  "https://images.unsplash.com/photo-1613977257363-707ba9348227?auto=format&fit=crop&w=2400&q=80",
];

// Slow-motion looping luxury clips (aerial coast + yacht + villa pool + palms)
// Mixkit CDN — free preview clips, stable, high-resolution.
const HERO_VIDEOS = [
  "https://assets.mixkit.co/videos/preview/mixkit-aerial-shot-of-a-yacht-in-a-tropical-sea-52082-large.mp4",
  "https://assets.mixkit.co/videos/preview/mixkit-luxury-boat-crossing-the-sea-51944-large.mp4",
  "https://assets.mixkit.co/videos/preview/mixkit-aerial-view-of-a-mansion-and-swimming-pool-1462-large.mp4",
];

export default function KenBurnsHero({ children }) {
  const [idx, setIdx] = useState(0); // fallback image index
  const [vIdx, setVIdx] = useState(0);
  const [videoOk, setVideoOk] = useState(true);
  const timer = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return; // no cycling for reduced motion
    timer.current = window.setInterval(() => {
      setIdx((i) => (i + 1) % HERO_IMAGES.length);
    }, 7000);
    return () => window.clearInterval(timer.current);
  }, []);

  // Slow-motion effect on video
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const setSlow = () => {
      try {
        v.playbackRate = 0.7;
      } catch {}
    };
    v.addEventListener("loadeddata", setSlow);
    setSlow();
    return () => v.removeEventListener("loadeddata", setSlow);
  }, [vIdx]);

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen overflow-hidden isolate"
    >
      {/* VIDEO layer */}
      {videoOk && (
        <video
          ref={videoRef}
          key={HERO_VIDEOS[vIdx]}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster={HERO_IMAGES[0]}
          onError={() => setVideoOk(false)}
          onEnded={() => setVIdx((i) => (i + 1) % HERO_VIDEOS.length)}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ zIndex: 0 }}
          data-testid="hero-video"
        >
          <source src={HERO_VIDEOS[vIdx]} type="video/mp4" />
        </video>
      )}

      {/* IMAGE fallback / Ken Burns layer (visible if video fails, and always as extra depth) */}
      {!videoOk && (
        <div aria-hidden className="absolute inset-0" style={{ zIndex: 0 }}>
          {HERO_IMAGES.map((src, i) => (
            <div
              key={src}
              className={`kb-slide ${i === idx ? "active" : ""}`}
              style={{ backgroundImage: `url(${src})` }}
            />
          ))}
        </div>
      )}

      {/* Overlay */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          zIndex: 1,
          background:
            "linear-gradient(180deg, rgba(15,16,18,0.55) 0%, rgba(15,16,18,0.35) 40%, rgba(15,16,18,0.7) 100%)",
        }}
      />

      {/* Particle dust */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{ zIndex: 2 }}
      >
        {Array.from({ length: 18 }).map((_, i) => {
          const size = 2 + (i % 5);
          const top = (i * 53) % 100;
          const left = (i * 37) % 100;
          const delay = (i * 0.8) % 12;
          return (
            <span
              key={i}
              className="dust"
              style={{
                width: size,
                height: size,
                top: `${top}%`,
                left: `${left}%`,
                animationDelay: `${delay}s`,
              }}
            />
          );
        })}
      </div>

      <div className="relative z-10 min-h-screen max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-24 pt-32">
        {children}
      </div>

      {/* Video indicator dots (only when video is playing) */}
      {videoOk && (
        <div
          className="absolute right-6 md:right-10 bottom-6 flex items-center gap-2"
          style={{ zIndex: 3 }}
        >
          {HERO_VIDEOS.map((_, i) => (
            <button
              key={i}
              data-testid={`hero-dot-${i}`}
              onClick={() => setVIdx(i)}
              aria-label={`Scene ${i + 1}`}
              className={`h-[2px] transition-all duration-500 ${
                i === vIdx ? "w-10 bg-white" : "w-4 bg-white/40 hover:bg-white/70"
              }`}
            />
          ))}
        </div>
      )}
    </section>
  );
}
